import { buildApiUrl, ensureVisit } from './api-host';
import { trackGoal } from './metrika';
import { trackBankiRuAbGoal } from './bankiru-ab-goals';

// Первый запрос выполняется сразу. Последующие запросы идут по единому графику
// независимо от типа продукта: часто в первую минуту и реже при долгом ожидании.
const POLL_SCHEDULE_MS = [
  3000, 6000, 9000, 12000, 15000,
  20000, 25000, 30000,
  40000, 50000, 60000,
  75000, 90000, 105000, 120000,
  150000, 180000, 210000, 240000, 270000, 300000,
];
const LEAD_FALLBACK_POLL_INTERVAL_MS = 3000;
// Сколько держим карточку заблокированной, если ответ на клик так и не пришёл.
const CLICK_GUARD_MS = 10000;

function pluralize(value, forms) {
  const n = Math.abs(Number(value)) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}

function updateBankiRuModalCount(value) {
  $('#bankiru-modal-count').text(value);
  $('#bankiru-modal-count-label').text(pluralize(value, ['предложение', 'предложения', 'предложений']));
}

export function formatBankiRuTerm(value, type) {
  const amount = Number(value) || 0;
  const forms = type === 'MONTH'
    ? ['месяц', 'месяца', 'месяцев']
    : ['день', 'дня', 'дней'];
  return `${amount} ${pluralize(amount, forms)}`;
}

function getWrapperByKey(key) {
  if (!key) return { open: '', close: '' };
  switch (String(key)) {
    case 'monetka':
    case 'cash2you':
      return { open: '<div class="tpl-offers tpl-row tpl-align-horisontal-start tpl-js-offers-wrapper">', close: '</div>' };
    case 'denga':
      return { open: '<div class="tpl-offers">', close: '</div>' };
    case 'tzaem':
      return { open: '<div class="offers__items">', close: '</div>' };
    case 'template1':
      return { open: '<div class="offers__list">', close: '</div>' };
    default:
      if (typeof key === 'string' && key.startsWith('div.')) {
        const classes = key.slice(4);
        return { open: `<div class="${classes}">`, close: '</div>' };
      }
      return { open: '', close: '' };
  }
}

function getBankiRuParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const alg = urlParams.get('alg') || '';
  // Dev-only: scenario selector for MSW (not used by backend)
  const bankiruCase = urlParams.get('bankiru_case') || '';
  // Dev/QA-only: ?bankiru=1 форсирует и клиентский рендер (ниже по файлу), и
  // серверную проверку ролаута в BankiRuController::get() — независимо от
  // website.bankiRuEnabled/bankiRuPercent. Пробрасываем тем же параметром,
  // чтобы не заводить второй флаг.
  const forceRollout = urlParams.get('bankiru') === '1' || urlParams.get('bankiru_enable') === '1';
  const resetMock = urlParams.get('reset_mock') === '1';

  // Для локального тестирования: берем phone и name из GET-параметров если window переменные не заданы
  if (!window.__bankiruPhone) {
    window.__bankiruPhone = urlParams.get('ph');
  }

  return { alg, bankiruCase, forceRollout, resetMock };
}

// Общие для initBankiRu и initBankiRuForLead — обе функции трекают события
// (bankiru_offer_impression / bankiru_lead_offer_impression), поэтому на уровне модуля,
// а не внутри одной из них (иначе вторая не увидит их в своей области видимости).
function post(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(r => r.json());
}

// B24-92368: отправка события показа в backend без парсинга JSON — /api/bankiru/event
// отвечает 204 No Content.
function postEvent(body) {
  return fetch(buildApiUrl('/api/bankiru/event') || '/api/bankiru/event', {
    method: 'POST',
    // Без Content-Type: application/json намеренно. API-хост кросс-доменный, и
    // этот заголовок делает запрос preflight'ным, а fetch с keepalive+preflight
    // в Chrome/Android WebView младше 120 отвергается синхронно — .catch ниже
    // проглотил бы это молча, и события перестали бы доходить ВСЕ, а не только
    // при выгрузке. Без заголовка это simple request: preflight не нужен.
    // Обе стороны читают тело как есть — json_decode($request->getContent()),
    // ни прокси a_site, ни a_admin на Content-Type не смотрят.
    body: JSON.stringify(body),
    // Показ отправляется параллельно клику и уходу со страницы: без keepalive
    // браузер обрывает запрос при выгрузке документа, и событие теряется вместе
    // с шагом воронки. Тело события — десятки байт, в лимит 64 КБ укладывается.
    keepalive: true,
  }).catch(() => {});
}

// smsToken обязателен: в fallback-режиме (продукты подобраны заново) bankiruLeadId
// принадлежит НОВОМУ лиду, а visitId вовсе не приходит — по ним показ невозможно
// связать с СМС, и в отчёте он терялся целиком. Токен один и тот же в обоих
// сценариях, поэтому отчёт матчит показ по нему (BankiRuAnalyticsController::smsFunnel).
function trackLeadImpression(bankiRuLeadId, visitId, count, smsToken) {
  const payload = {
    type: 'bankiru_lead_offer_impression',
    bankiruLeadId: bankiRuLeadId,
    ...(visitId ? { visitId } : {}),
    ...(smsToken ? { smsToken } : {}),
    occurredAt: new Date().toISOString(),
    count,
  };

  trackGoal('bankiru_lead_offer_impression', { bankiruLeadId: bankiRuLeadId, count });

  // Отправляем и не ждём: ответ 204 приходит сразу после dispatch в очередь и не
  // означает, что показ сохранён, — ждать его было нечем оправдать, а клик всё
  // это время стоял.
  postEvent(payload);
}

async function trackEvent(type, extra = {}) {
  // occurredAt — до ожидания визита, иначе при полном провале (бюджет 15s)
  // время события уедет на время ожидания, а не отразит момент, когда всё случилось.
  const occurredAt = new Date().toISOString();
  // B24-92552: ждём visitId (бюджет ограничен внутри ensureVisit), но если так и не
  // добились — поле не шлём вовсе (как везде в этом файле), а не visitId: null:
  // событие без атрибуции к визиту не нужно бэкенду для аналитики.
  const visitId = await ensureVisit();
  post(buildApiUrl('/api/bankiru/event') || '/api/bankiru/event', {
    type,
    ...(visitId ? { visitId } : {}),
    occurredAt,
    ...extra,
  }).catch(() => {});

  // Тот же тип события уходит и целью в Яндекс.Метрику — единая точка для обеих систем.
  trackGoal(type, extra);
}

// === Long-flow preloader: круговой двухэтапный таймер ===
// Создаётся лениво — только при первом pending + isPrescoringStarted=true.
// Идемпотентно: повторная инициализация не создаёт дубли DOM.
const LONG_STAGE_PRIMARY_MS = 60000;   // 01:00
const LONG_STAGE_SECONDARY_MS = 30000; // 00:30
const LONG_TOTAL_MS = LONG_STAGE_PRIMARY_MS + LONG_STAGE_SECONDARY_MS; // 90s

const LONG_TEXTS = [
  { until: 15000,  title: 'Ваша заявка одобрена', text: 'Подбираем предложения банков...' },
  { until: 40000,  title: 'Одобренные предложения уже есть', text: 'Готовим суммы и условия...' },
  { until: 60000,  title: 'Подбираем лучшие условия', text: 'Среди одобренных предложений...' },
  { until: 75000,  title: 'Одобренные предложения готовы', text: 'Загружаем результаты...' },
  { until: 90000,  title: 'Ваши предложения почти готовы', text: 'Осталось совсем немного...' },
  { until: Infinity, title: 'Ещё немного', text: 'Загружаем одобренные предложения...' },
];

function longEntryFor(elapsed) {
  for (const entry of LONG_TEXTS) {
    if (elapsed < entry.until) return entry;
  }
  return LONG_TEXTS[LONG_TEXTS.length - 1];
}

/**
 * Создаёт long-flow state machine внутри существующего $preloader.
 * Возвращает объект с методами start/stop/destroy.
 * Не создаёт DOM, если контейнер отсутствует — безопасный no-op.
 */
function createLongFlow($preloader) {
  let state = 'idle';        // idle → primary_60s → secondary_30s → indefinite → stopped
  let startTime = 0;
  let intervalId = null;
  let hasSeenLongPrescoring = false;

  // DOM elements (created lazily)
  let $ringWrap = null;
  let $ringCircle = null;
  let $ringTimer = null;
  let $longTitle = null;
  let $longText = null;
  let $flowWrap = null;
  let $origIcon = null;
  let $origText = null;
  let $sectionTitle = null;
  let $promoParent = null;
  let $promoNextSibling = null;
  let $conflictStatus = null;
  let conflictStatusOriginalDisplay = null;

  const RING_SIZE = 100;
  const RING_STROKE = 6;
  const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
  const RING_CIRC = 2 * Math.PI * RING_RADIUS;

  function ensureDom() {
    if ($ringWrap) return true;
    if (!$preloader || !$preloader.length) return false;

    $origIcon = $preloader.find('.bankiru-preloader__icon').first();
    $origText = $preloader.find('.bankiru-preloader__text').first();
    if (!$origIcon.length || !$origText.length) return false;

    // SVG-кольцо: серый фон + цветная заливка, растущая по мере прогресса
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.setAttribute('width', String(RING_SIZE));
    svg.setAttribute('height', String(RING_SIZE));
    svg.setAttribute('viewBox', `0 0 ${RING_SIZE} ${RING_SIZE}`);
    svg.classList.add('bankiru-long-ring__svg');

    // Серый фоновый круг
    const bgCircle = document.createElementNS(svgNs, 'circle');
    bgCircle.setAttribute('cx', String(RING_SIZE / 2));
    bgCircle.setAttribute('cy', String(RING_SIZE / 2));
    bgCircle.setAttribute('r', String(RING_RADIUS));
    bgCircle.setAttribute('fill', 'none');
    bgCircle.setAttribute('stroke', '#e0e0e0');
    bgCircle.setAttribute('stroke-width', String(RING_STROKE));
    bgCircle.classList.add('bankiru-long-ring__bg');

    // Цветной круг — заливается фирменным цветом от 0 до полного
    const fgCircle = document.createElementNS(svgNs, 'circle');
    fgCircle.setAttribute('cx', String(RING_SIZE / 2));
    fgCircle.setAttribute('cy', String(RING_SIZE / 2));
    fgCircle.setAttribute('r', String(RING_RADIUS));
    fgCircle.setAttribute('fill', 'none');
    fgCircle.setAttribute('stroke', 'var(--bankiru-accent, #2799ff)');
    fgCircle.setAttribute('stroke-width', String(RING_STROKE));
    fgCircle.setAttribute('stroke-linecap', 'round');
    fgCircle.setAttribute('stroke-dasharray', String(RING_CIRC));
    // Начинаем с пустого (offset = full circumference = invisible)
    fgCircle.setAttribute('stroke-dashoffset', String(RING_CIRC));
    fgCircle.classList.add('bankiru-long-ring__fg');

    svg.appendChild(bgCircle);
    svg.appendChild(fgCircle);

    // Timer text (центрируется внутри круга через CSS)
    const timerEl = document.createElement('span');
    timerEl.className = 'bankiru-long-ring__timer';
    timerEl.setAttribute('aria-hidden', 'true');
    timerEl.textContent = '01:00';

    // Wrapper — только круг + таймер внутри
    const wrap = document.createElement('div');
    wrap.className = 'bankiru-long-ring';
    // aria-live установлен на родительском .bankiru-long-flow — здесь не дублируем
    wrap.appendChild(svg);
    wrap.appendChild(timerEl);

    // Контейнер для long-flow: круг + заголовок + текст (без gap прелоадера)
    const flowWrap = document.createElement('div');
    flowWrap.className = 'bankiru-long-flow';
    flowWrap.setAttribute('role', 'status');
    flowWrap.setAttribute('aria-live', 'polite');

    // Заголовок long-flow (отдельный от заголовка секции)
    const titleEl = document.createElement('p');
    titleEl.className = 'bankiru-long-title';
    titleEl.textContent = LONG_TEXTS[0].title;

    // Текст long-flow (отдельный от оригинального текста прелоадера)
    const textEl = document.createElement('p');
    textEl.className = 'bankiru-long-text';
    textEl.textContent = LONG_TEXTS[0].text;

    // Вставляем контейнер перед оригинальной иконкой
    $origIcon[0].parentNode.insertBefore(flowWrap, $origIcon[0]);
    flowWrap.appendChild(wrap);
    flowWrap.appendChild(titleEl);
    flowWrap.appendChild(textEl);
    $origIcon.hide();
    $origText.hide();

    // Скрыть заголовок секции во время long-flow
    $sectionTitle = $preloader.closest('#bankiru-block').find('.bankiru-block__title').first();
    if ($sectionTitle.length) $sectionTitle.hide();

    $ringWrap = $(wrap);
    $ringCircle = $(fgCircle);
    $ringTimer = $(timerEl);
    $longTitle = $(titleEl);
    $longText = $(textEl);
    $flowWrap = $(flowWrap);

    return true;
  }

  function fmtTime(ms) {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }

  function computeStage(elapsed) {
    if (elapsed < LONG_STAGE_PRIMARY_MS) return 'primary_60s';
    if (elapsed < LONG_TOTAL_MS) return 'secondary_30s';
    return 'indefinite';
  }

  function render() {
    if (state === 'idle' || state === 'stopped') return;
    if (!$ringWrap) return;

    const elapsed = Date.now() - startTime;
    const stage = computeStage(elapsed);

    // Stage transition
    if (stage === 'secondary_30s' && state !== 'secondary_30s') {
      state = 'secondary_30s';
      $ringWrap.removeClass('bankiru-long-ring--primary');
      $ringWrap.addClass('bankiru-long-ring--secondary');
      // Сбросить прогресс для второго этапа
      $ringCircle.attr('stroke-dashoffset', String(RING_CIRC));
    } else if (stage === 'indefinite' && state !== 'indefinite') {
      state = 'indefinite';
      $ringWrap.removeClass('bankiru-long-ring--primary bankiru-long-ring--secondary');
      $ringWrap.addClass('bankiru-long-ring--indefinite');
      // Остановить посекундный interval — дальше CSS-пульсация
      stopInterval();
      // Показать обычную иконку, скрыть кольцо
      if ($origIcon) $origIcon.show();
      $ringWrap.hide();
    }

    // Update title + text
    const entry = longEntryFor(elapsed);
    if ($longTitle) $longTitle.text(entry.title);
    if ($longText) $longText.text(entry.text);

    if (state === 'primary_60s') {
      const remaining = LONG_STAGE_PRIMARY_MS - elapsed;
      const progress = Math.min(1, elapsed / LONG_STAGE_PRIMARY_MS);
      $ringTimer.text(fmtTime(remaining));
      // Заливка: от пустого (offset=CIRC) к полному (offset=0)
      $ringCircle.attr('stroke-dashoffset', String(RING_CIRC * (1 - progress)));
    } else if (state === 'secondary_30s') {
      const stageElapsed = elapsed - LONG_STAGE_PRIMARY_MS;
      const remaining = LONG_STAGE_SECONDARY_MS - stageElapsed;
      const progress = Math.min(1, stageElapsed / LONG_STAGE_SECONDARY_MS);
      $ringTimer.text(fmtTime(remaining));
      $ringCircle.attr('stroke-dashoffset', String(RING_CIRC * (1 - progress)));
    }
  }

  function startInterval() {
    if (intervalId) return;
    intervalId = setInterval(render, 1000);
  }

  function stopInterval() {
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') render();
  }

  function start() {
    if (hasSeenLongPrescoring) return;
    hasSeenLongPrescoring = true;
    if (!ensureDom()) return;
    state = 'primary_60s';
    startTime = Date.now();
    $ringWrap.show();
    $ringWrap.addClass('bankiru-long-ring--primary');
    document.body.classList.add('bankiru-long-active');
    // Переместить #promo после секции Banki.ru — перед обычными продуктами
    const promoEl = document.getElementById('promo');
    if (promoEl) {
      $promoParent = $(promoEl).parent();
      $promoNextSibling = promoEl.nextSibling;
      const $bankiruSection = $preloader.closest('[data-bankiru-section]');
      if ($bankiruSection.length) {
        $bankiruSection[0].parentNode.insertBefore(promoEl, $bankiruSection[0].nextSibling);
      }
      // Отступы при long-flow: 200px от прелоадера до promo
      promoEl.style.marginTop = '200px';
      promoEl.style.marginBottom = '24px';
      promoEl.style.paddingTop = '0';
      promoEl.style.paddingBottom = '0';
    }
    // Скрыть конфликтующий блок витрины (tzaem: «не смогли одобрить» + кредитный рейтинг)
    // во время long-flow. Перенос после секции — в movePromoAfterSection при success.
    const bankiruSectionEl = $preloader.closest('[data-bankiru-section]')[0];
    if (bankiruSectionEl) {
      const p = bankiruSectionEl.parentNode;
      let headingEl = null;
      for (const child of p.children) {
        if (child.classList.contains('offers__heading')) { headingEl = child; break; }
      }
      if (headingEl && getComputedStyle(headingEl).display !== 'none') {
        $conflictStatus = $(headingEl);
        conflictStatusOriginalDisplay = headingEl.style.display || getComputedStyle(headingEl).display;
        headingEl.style.display = 'none';
      }
    }
    render();
    startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function restorePromo() {
    const promoEl = document.getElementById('promo');
    if (promoEl && $promoParent) {
      $promoParent[0].insertBefore(promoEl, $promoNextSibling);
      promoEl.style.marginTop = '';
      promoEl.style.marginBottom = '';
      promoEl.style.paddingTop = '';
      promoEl.style.paddingBottom = '';
    }
    $promoParent = null;
    $promoNextSibling = null;
  }

  function restoreConflictStatus() {
    if ($conflictStatus && $conflictStatus.length) {
      $conflictStatus[0].style.display = conflictStatusOriginalDisplay;
    }
    $conflictStatus = null;
    conflictStatusOriginalDisplay = null;
  }

  function stop() {
    stopInterval();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    state = 'stopped';
    document.body.classList.remove('bankiru-long-active');
    restorePromo();
    restoreConflictStatus();
    if ($origIcon) $origIcon.show();
    if ($origText) $origText.show();
    if ($sectionTitle && $sectionTitle.length) $sectionTitle.show();
    if ($flowWrap) $flowWrap.remove();
    $ringWrap = null;
    $ringCircle = null;
    $ringTimer = null;
    $longTitle = null;
    $longText = null;
    $flowWrap = null;
    $sectionTitle = null;
  }

  function hide() {
    stopInterval();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    state = 'done';
    document.body.classList.remove('bankiru-long-active');
    restoreConflictStatus();
    // При success: promo остаётся после секции — компактный отступ
    const promoEl = document.getElementById('promo');
    if (promoEl) {
      promoEl.style.marginTop = '40px';
      promoEl.style.marginBottom = '24px';
      promoEl.style.paddingTop = '0';
      promoEl.style.paddingBottom = '0';
    }
    if ($flowWrap) $flowWrap.hide();
    if ($origIcon) $origIcon.show();
    if ($origText) $origText.show();
    if ($sectionTitle && $sectionTitle.length) $sectionTitle.show();
  }

  return { start, stop, hide, get state() { return state; }, get isActive() { return hasSeenLongPrescoring && state !== 'stopped' && state !== 'done'; } };
}

/**
 * Плавный скролл к началу секции Banki.ru с учётом фиксированной шапки.
 * При prefers-reduced-motion — мгновенный скролл.
 */
function scrollToBankiRuSection($section) {
  if (!$section || !$section.length) return;
  // Не скроллим к скрытой секции (например, все продукты просрочены)
  if (!$section.is(':visible')) return;
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Определяем высоту фиксированной шапки динамически
  const $header = $('header, .tpl-header, .header, .site-header').filter(function() {
    return getComputedStyle(this).position === 'fixed' || getComputedStyle(this).position === 'sticky';
  });
  const headerHeight = $header.length ? Math.round($header.outerHeight()) : 0;
  const top = $section.offset().top - headerHeight - 8;
  if (reduceMotion) {
    window.scrollTo(0, top);
  } else {
    $('html, body').animate({ scrollTop: top }, 400);
  }
}

export async function initBankiRu({ renderCard, wrapperOpen = '', wrapperClose = '' }) {
  // === B24-91754/92020: режим страницы заявки mnedali.ru/{token} ===
  // Токен — 6 символов base62 (непрогнозируемый, не раскрывает id заявки, как раньше
  // c.mnedali.ru/BlhjE8). Два замка: формат токена И #lead-offers-header в DOM.
  // На mnedali.ru (без CDN) токен приходит в пути. На витринах-получателях
  // (за Yandex CDN + Object Storage, только статика) серверный роут /{token}
  // недостижим — CDN отдаёт закэшированный index.html по любому пути, минуя
  // приложение. Поэтому туда редиректим с токеном в query (?lt=), его читает
  // уже отданная статическая страница на клиенте.
  const TOKEN_RE = /^[A-Za-z0-9]{6}$/;
  const pathMatch = window.location.pathname.match(/^\/([A-Za-z0-9]{6})$/);
  const queryToken = new URLSearchParams(window.location.search).get('lt');
  const leadToken = pathMatch ? pathMatch[1] : (queryToken && TOKEN_RE.test(queryToken) ? queryToken : null);

  if (leadToken) {
    initBankiRuForLead({ renderCard, wrapperOpen, wrapperClose, leadToken });
    return;
  }

  const { alg, bankiruCase, forceRollout, resetMock } = getBankiRuParams();

  // A/B-цели банкира ({slug}_bankiru_on / {slug}_bankiru_off) — отправляем по
  // серверному значению bankiRuInRollout ДО клиентского форсирования ?bankiru=1,
  // чтобы цели A/B-теста отражали реальное распределение, а не dev/QA-обход.
  // На странице лида (leadToken) не отправляем — там отдельный поток.
  trackBankiRuAbGoal(window.__bankiRuInRollout === true);

  // Dev helper: allow enabling rollout from URL (?bankiru=1)
  const __url = new URLSearchParams(window.location.search);
  if (__url.get('bankiru') === '1' || __url.get('bankiru_enable') === '1') {
    window.__bankiRuInRollout = true;
  }

  // Rollout flag from visit/track response (set in api-host.js).
  // If explicitly false — do not render or call API
  if (window.__bankiRuInRollout === false) {
    $('[data-bankiru-section], .offers__bankiru, #bankiru-section').remove();
    return;
  }

  if (!window.__bankiruPhone) {
    $('.tpl-sect__bankiru, #bankiru-section').remove();
    return;
  }

  // Require rollout true; no legacy __bankiRuEnabled gate
  if (window.__bankiRuInRollout !== true) return;

  // Section selection: prefer data attribute to allow per-vitrina wrappers
  const $section = $('[data-bankiru-section], .offers__bankiru, .bankiru-section');
  const $block = $('#bankiru-block');
  const $preloader = $('#bankiru-preloader');
  // Cards root hook: accept either data-bankiru-offers (legacy) or data-banki-ru-offers (new)
  const $cardsContainer = $block.find('[data-bankiru-offers], [data-banki-ru-offers]');
  const $modal = $('#bankiru-modal');
  let __bankiruScrollY = 0;
  // Клики, по которым ответ ещё не пришёл. Живёт вне bindClicks: тот вызывается
  // повторно (модалка, revealCards) и локальный набор обнулялся бы каждый раз.
  const clicksInFlight = new Set();

  // Long-flow state machine (создаётся лениво, только при started=true)
  const longFlow = createLongFlow($preloader);

  if (!$section.length) return;

  // Секция скрыта до первого ответа API — избегаем вспышки пустого блока
  // при error/timeout/пустом результате.
  $section.hide();
  $preloader.hide();

  // B24-92552: visitId обязателен — ждём его перед первым запросом (бюджет
  // ограничен внутри ensureVisit, дальнейший опрос ниже читает window.__visitId
  // живьём и сам подхватит визит, если он появится позже).
  const initialVisitId = await ensureVisit();

  // 1. Получаем офферы (или узнаём, что идёт прескоринг)
  const firstQuery = new URLSearchParams({
    ph: window.__bankiruPhone,
    ...(alg ? { alg } : {}),
    ...(initialVisitId ? { visitId: initialVisitId } : {}),
    ...(bankiruCase ? { bankiru_case: bankiruCase } : {}),
    ...(forceRollout ? { forceRollout: '1' } : {}),
    ...(resetMock ? { reset_mock: '1' } : {}),
  }).toString();
  const pollingStartedAt = Date.now();
  fetch((buildApiUrl('/api/bankiru/get') || '/api/bankiru/get') + '?' + firstQuery, { method: 'GET' })
    .then(r => r.json())
    .then(res => {
      const result = String(res?.result || '').toLowerCase();
      const started = !!res?.isPrescoringStarted;
      if (result === 'success' && Array.isArray(res.prescoringList) && res.prescoringList.length) {
        // Успех: показываем офферы сразу в секции
        longFlow.stop();
        $preloader.hide();
        $section.css({ 'margin-top': '', 'margin-bottom': '24px' });
        $section.show();
        showCards(res.prescoringList, undefined);
        return;
      }
      if (result === 'pending') {
        if (started) {
          // pending + isPrescoringStarted=true: long-flow — показать секцию с таймером
          $section.css({ 'margin-top': '', 'margin-bottom': '24px' });
          $section.show();
          $preloader.show();
          longFlow.start();
          backgroundPoll(true, pollingStartedAt);
          return;
        }
        // pending + isPrescoringStarted=false: показать прелоадер и секцию, фоновый опрос
        $section.css({ 'margin-top': '', 'margin-bottom': '24px' });
        $section.show();
        $preloader.show();
        backgroundPoll(false, pollingStartedAt);
        return;
      }
      // error/timeout: не показываем прелоадер, скрываем блок
      longFlow.stop();
      $preloader.hide();
      hideBlock();
    })
    .catch(() => { longFlow.stop(); $preloader.hide(); hideBlock(); });

  function backgroundPoll(initialStarted, start) {
    let lastPrescoringStarted = initialStarted;
    let timer = null;
    let scheduleIndex = 0;

    const stopPolling = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const schedule = () => {
      // Если предыдущий HTTP-запрос занял дольше интервала, пропускаем уже
      // прошедшие точки графика, чтобы не отправлять несколько запросов подряд.
      while (
        scheduleIndex < POLL_SCHEDULE_MS.length &&
        start + POLL_SCHEDULE_MS[scheduleIndex] <= Date.now()
      ) {
        scheduleIndex += 1;
      }

      if (scheduleIndex >= POLL_SCHEDULE_MS.length) {
        stopPolling();
        longFlow.stop();
        $preloader.hide();
        hideBlock();
        return;
      }

      const scheduledAt = start + POLL_SCHEDULE_MS[scheduleIndex];
      scheduleIndex += 1;
      timer = setTimeout(tick, Math.max(0, scheduledAt - Date.now()));
    };

    function tick() {
      const q = new URLSearchParams({
        ph: window.__bankiruPhone,
        ...(alg ? { alg } : {}),
        ...(window.__visitId ? { visitId: window.__visitId } : {}),
        ...(bankiruCase ? { bankiru_case: bankiruCase } : {}),
        ...(forceRollout ? { forceRollout: '1' } : {}),
      }).toString();
      fetch((buildApiUrl('/api/bankiru/get') || '/api/bankiru/get') + '?' + q, { method: 'GET' })
        .then(r => r.json())
        .then(res => {
          const result = String(res?.result || '').toLowerCase();
          if (result === 'success' && Array.isArray(res.prescoringList) && res.prescoringList.length) {
            stopPolling();
            // Long-flow: карточки сразу в секции, без модалки
            if (longFlow.isActive) {
              longFlow.hide();
              $preloader.hide();
              $section.show();
              showCards(res.prescoringList, undefined);
              scrollToBankiRuSection($section);
            } else if (lastPrescoringStarted) {
              // Быстрый сценарий с started=true — модалка сохранена
              showModal(res.prescoringList, undefined);
            } else {
              $section.show();
              showCards(res.prescoringList, undefined);
            }
            return;
          }
          if (result === 'pending') {
            // Липкий long-flow: после первого started=true не сбрасываем
            const nowStarted = !!res?.isPrescoringStarted;
            if (nowStarted && !longFlow.isActive) {
              longFlow.start();
            }
            if (nowStarted) {
              lastPrescoringStarted = true;
              $section.show();
              $preloader.show();
            }
            // keep polling
            schedule();
            return;
          }
          // error or other – stop polling; прячем прелоадер и секцию
          stopPolling();
          longFlow.stop();
          $preloader.hide();
          hideBlock();
        })
        .catch(() => { stopPolling(); longFlow.stop(); $preloader.hide(); hideBlock(); });
    }

    schedule();
  }

  function showCards(list, bankiruLeadId) {
    $preloader.hide();
    // Skip offers that are already expired at render time
    const nowTs = Date.now();
    const filtered = Array.isArray(list)
      ? list.filter(p => {
          const ts = p && p.approvalTermExpire ? new Date(p.approvalTermExpire).getTime() : null;
          return ts == null || ts > nowTs;
        })
      : [];
    if (!filtered.length) {
      // Nothing to show — секция скрывается, promo не перемещается
      $section.hide();
      if ($modal && $modal.length) { $modal.hide(); }
      return;
    }
    // Переместить #promo после секции — только когда есть продукты для показа
    movePromoAfterSection();
    // Update title verb to reflect offers are approved/shown
    const $verb = $('[data-bankiru-title-verb]');
    if ($verb && $verb.length) $verb.text('Одобрили');
    // Optional per-family wrapper rendering
    if (wrapperOpen || wrapperClose) {
      const cardsHtml = filtered.map(renderCard).join('');
      $cardsContainer.html((wrapperOpen || '') + cardsHtml + (wrapperClose || ''));
      startCountdownTimers();
      trackEvent('bankiru_offer_impression', { bankiruLeadId, count: filtered.length });
      bindClicks(bankiruLeadId);
      return;
    }
    const wrapperKey =
      $cardsContainer.data('bankiruWrapper') ||
      $cardsContainer.data('wrapper') ||
      $section.data('bankiruWrapper') ||
      $section.data('wrapper') ||
      '';
    const wrap = getWrapperByKey(wrapperKey);
    const cardsHtml = filtered.map(renderCard).join('');
    $cardsContainer.html(wrap.open + cardsHtml + wrap.close);
    startCountdownTimers();
    trackEvent('bankiru_offer_impression', { bankiruLeadId, count: filtered.length });
    bindClicks(bankiruLeadId);
  }

  function showModal(list, bankiruLeadId) {
    // Переместить #promo после секции Banki.ru
    movePromoAfterSection();
    const nowTs = Date.now();
    const filtered = Array.isArray(list)
      ? list.filter(p => {
          const ts = p && p.approvalTermExpire ? new Date(p.approvalTermExpire).getTime() : null;
          return ts == null || ts > nowTs;
        })
      : [];
    if (!filtered.length) {
      // Nothing to show — ensure modal stays hidden
      if ($modal && $modal.length) { $modal.hide(); }
      return;
    }
    updateBankiRuModalCount(filtered.length);
    // Update title verb to reflect offers arrival
    const $verb = $('[data-bankiru-title-verb]');
    if ($verb && $verb.length) $verb.text('Одобрили');
    // Рендерим карточки в основной блок c учётом обёртки
    if (wrapperOpen || wrapperClose) {
      const cardsHtml = filtered.map(renderCard).join('');
      $cardsContainer.html((wrapperOpen || '') + cardsHtml + (wrapperClose || ''));
      // Start timers immediately so countdown is visible even before closing modal
      startCountdownTimers();
      $preloader.hide();
      $section.show();
      // Ensure clicks are ready once modal is dismissed or if user closes it
      bindClicks(bankiruLeadId);
      $modal.css('display', 'flex');
      trackEvent('bankiru_modal_shown', { bankiruLeadId });
      $('#bankiru-modal-cta').one('click', () => {
        $modal.hide();
        trackEvent('bankiru_modal_click', { bankiruLeadId });
        startCountdownTimers();
        bindClicks(bankiruLeadId);
        trackEvent('bankiru_offer_impression', { bankiruLeadId, count: filtered.length });
        // Smooth-scroll to BankiRu section after showing cards
        $('html,body').animate({ scrollTop: $section.offset().top }, 400);
      });
      $('.bankiru-modal__close, .bankiru-modal__overlay').one('click', () => {
        $modal.hide();
        bindClicks(bankiruLeadId);
      });
      return;
    }
    const wrapperKey =
      $cardsContainer.data('bankiruWrapper') ||
      $cardsContainer.data('wrapper') ||
      $section.data('bankiruWrapper') ||
      $section.data('wrapper') ||
      '';
    const wrap = getWrapperByKey(wrapperKey);
    const cardsHtml = filtered.map(renderCard).join('');
    $cardsContainer.html(wrap.open + cardsHtml + wrap.close);
    // Start timers immediately so countdown is visible even before closing modal
    startCountdownTimers();
    $preloader.hide();
    $section.show();
    bindClicks(bankiruLeadId);
    $modal.css('display', 'flex');
    trackEvent('bankiru_modal_shown', { bankiruLeadId });
    $('#bankiru-modal-cta').one('click', () => {
      $modal.hide();
      trackEvent('bankiru_modal_click', { bankiruLeadId });
      startCountdownTimers();
      bindClicks(bankiruLeadId);
      trackEvent('bankiru_offer_impression', { bankiruLeadId, count: filtered.length });
      $('html,body').animate({ scrollTop: $section.offset().top }, 400);
    });
    $('.bankiru-modal__close, .bankiru-modal__overlay').one('click', () => {
      $modal.hide();
      bindClicks(bankiruLeadId);
    });
  }

  function revealCards(bankiruLeadId) {
    $('html,body').animate({ scrollTop: $section.offset().top }, 400);
    trackEvent('bankiru_offer_impression', { bankiruLeadId, count: $cardsContainer.children().length });
    bindClicks(bankiruLeadId);
  }

  function hideBlock() { $section.hide(); }

  // Переместить #promo после секции Banki.ru — перед обычными продуктами.
  // Идемпотентно: повторные вызовы не дублируют promo.
  function movePromoAfterSection() {
    const bankiruSection = $section[0];
    if (!bankiruSection) return;
    const parent = bankiruSection.parentNode;

    // Переместить #promo после секции Banki.ru
    const promo = document.getElementById('promo');
    if (promo) {
      if (promo.previousElementSibling !== bankiruSection) {
        parent.insertBefore(promo, bankiruSection.nextSibling);
      }
      promo.style.marginTop = '40px';
      promo.style.marginBottom = '24px';
      promo.style.paddingTop = '0';
      promo.style.paddingBottom = '0';
    }

    // Переместить .offers__heading (статус + кредитный рейтинг) после секции Banki.ru
    // На tzaem этот блок стоит ДО Banki.ru — должен быть после
    let heading = null;
    for (const child of parent.children) {
      if (child.classList.contains('offers__heading')) { heading = child; break; }
    }
    if (heading && heading.nextElementSibling !== bankiruSection) {
      parent.insertBefore(heading, bankiruSection.nextSibling);
    }
  }

  function bindClicks(bankiruLeadId) {
    // Открываем вкладку непосредственно в пользовательском обработчике: после
    // await/fetch браузер считает window.open попапом и блокирует переход.
    const go = (productId) => {
      // Повторный тап, пока клик в работе, теперь открывает вторую вкладку и
      // задваивает клик в статистике: window.open больше не глушится браузером.
      if (clicksInFlight.has(productId)) return;
      clicksInFlight.add(productId);
      // Предохранитель: если запрос завис, карточка не должна остаться мёртвой.
      const release = setTimeout(() => clicksInFlight.delete(productId), CLICK_GUARD_MS);
      const done = () => { clearTimeout(release); clicksInFlight.delete(productId); };

      const win = window.open('', '_blank');

      Promise.race([
        ensureVisit(),
        new Promise((resolve) => setTimeout(() => resolve(window.__visitId), 400)),
      ])
        .then(visitId => post(buildApiUrl('/api/bankiru/click') || '/api/bankiru/click', {
          bankiruProductId: productId,
          ...(visitId ? { visitId } : {}),
        }))
        .then(res => {
          done();
          if (res && res.result === 'success' && res.partnerUrl) {
            trackGoal('bankiru_offer_click', { bankiruLeadId, bankiruProductId: productId });
            if (win) {
              win.location.href = res.partnerUrl;
            } else {
              window.location.href = res.partnerUrl;
            }
          } else if (win) {
            win.close();
          }
        })
        .catch(() => { done(); if (win) win.close(); });
    };

    // Rebind with namespacing to avoid duplicates
    $cardsContainer.off('click.bankiru')
      // Fallback: explicit button click (runs first and cancels others)
      .on('click.bankiru', '.bankiru-card__btn', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const $wrapper = $(this).closest('[data-product-id], [data-product-code]');
        const productId = $wrapper.data('product-id') || $wrapper.data('product-code');
        if (!productId) return;
        go(productId);
      })
      // Make entire card clickable; ignore clicks that originated on the explicit button (already handled)
      .on('click.bankiru', '[data-product-id], [data-product-code]', function (e) {
        if ($(e.target).closest('.bankiru-card__btn').length) return;
        const $wrapper = $(e.target).closest('[data-product-id], [data-product-code]');
        if ($wrapper.length === 0) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const productId = $wrapper.data('product-id') || $wrapper.data('product-code');
        if (!productId) return;
        go(productId);
      });
  }

  function startCountdownTimers() {
    $('.bankiru-card__expire').each(function () {
      const expireTimestamp = $(this).data('expire');
      if (!expireTimestamp) return;

      const $countdown = $(this).find('.bankiru-card__countdown');
      const expireDate = new Date(expireTimestamp);

      const updateTimer = () => {
        const now = new Date();
        const diff = expireDate - now;

        // Hide the block entirely when more than 1 hour remains
        if (diff > 3600000) {
          $(this).hide();
          return;
        }

        if (diff <= 0) {
          // Remove whole card when expired
          const $card = $(this).closest('[data-product-id],[data-product-code],.tpl-offer__wrapper');
          $card.remove();
          // If no BankiRu cards remain, hide containers; otherwise update modal count
          const remaining = $cardsContainer.find('[data-product-id],[data-product-code],.tpl-offer__wrapper').length;
          if (remaining === 0) {
            $preloader.hide();
            $section.hide();
            if ($modal && $modal.length) { $modal.hide(); }
          } else if ($modal && $modal.length) {
            updateBankiRuModalCount(remaining);
          }
          return;
        }

        // Ensure visible within the last hour
        $(this).show();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        $countdown.text(
          String(hours).padStart(2, '0') + ':' +
          String(minutes).padStart(2, '0') + ':' +
          String(seconds).padStart(2, '0')
        );
      };

      updateTimer();
      setInterval(updateTimer, 1000);
    });
  }

  function fmt(v) { return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' '); }

  setInterval(() => {
    $('.bankiru-card__expire[data-expire]').each(function () {
      const diff = new Date($(this).data('expire')) - Date.now();
      // If expired: remove card and possibly hide containers
      if (diff <= 0) {
        const $card = $(this).closest('[data-product-id],[data-product-code],.tpl-offer__wrapper');
        $card.remove();
        const remaining = $cardsContainer.find('[data-product-id],[data-product-code],.tpl-offer__wrapper').length;
        if (remaining === 0) {
          $preloader.hide();
          $section.hide();
          if ($modal && $modal.length) { $modal.hide(); }
        } else if ($modal && $modal.length) {
          updateBankiRuModalCount(remaining);
        }
        return;
      }
      // Hide timer when more than 1 hour remains
      if (diff > 3600000) { $(this).hide(); return; }
      // Show when within the last hour
      $(this).show();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      $(this).find('.bankiru-card__countdown').text(
        String(h).padStart(2, '0') + ':' +
        String(m).padStart(2, '0') + ':' +
        String(s).padStart(2, '0')
      );
    });
  }, 30000);
}

async function initBankiRuForLead({ renderCard, wrapperOpen, wrapperClose, leadToken }) {
  // Клики, по которым ответ ещё не пришёл (bindLeadClicks вызывается и в
  // основном сценарии, и в fallback — набор общий на страницу).
  const clicksInFlight = new Set();
  const $section = $('[data-bankiru-section]');
  const $block = $('#bankiru-block');
  const $preloader = $('#bankiru-preloader');
  const $cardsContainer = $block.find('[data-bankiru-offers], [data-banki-ru-offers]');
  // На CDN-витринах страница отдается статическим HTML, поэтому Twig не может
  // добавить заголовок для ссылки с токеном. Создаём тот же заголовок на клиенте.
  let $header = $('#lead-offers-header');
  if (!$header.length) {
    const isMnedaliLeadPage = $section.closest('#vitrina-view').length > 0;
    const headerText = 'Уважаемый клиент, по вашей заявке №<span data-lead-id>—</span> '
      + 'одобрена сумма <nobr><span data-lead-amount>—</span> ₽</nobr>';
    $header = $(isMnedaliLeadPage
      ? '<div id="lead-offers-header" class="tpl-offers-heading__title" style="display:none"><h1>' + headerText + '</h1></div>'
      : '<div id="lead-offers-header" style="display:none"><h1 class="bankiru-block__title">' + headerText + '</h1></div>'
    );
    $block.before($header);
  }

  if (!$section.length || !$cardsContainer.length) return;

  trackGoal('bankiru_lead_page_view', { leadToken });

  // На странице заявки персональный заголовок заменяет общий заголовок Banki.ru.
  $block.find('.bankiru-block__title').hide();
  $preloader.show();
  $section.show();

  const showHeaderMessage = (title) => {
    $header.find('h1').text(title);
    $header.find('.tpl-offers-heading__text').hide();
    $header.show();
  };

  fetch(buildApiUrl('/api/bankiru/lead/' + leadToken) || ('/api/bankiru/lead/' + leadToken), { method: 'GET' })
    .then(r => r.json())
    .then(res => {
      $preloader.hide();

      if (!res || res.result !== 'success' || !Array.isArray(res.prescoringList)) {
        $section.hide();
        showHeaderMessage('Заявка не найдена');
        postLeadOutcome('bankiru_lead_not_found');
        return;
      }

      // Фильтр истёкших офферов
      const nowTs = Date.now();
      const list = res.prescoringList.filter(p => {
        const ts = p && p.approvalTermExpire ? new Date(p.approvalTermExpire).getTime() : null;
        return ts == null || ts > nowTs;
      });

      if (!list.length) {
        postLeadOutcome('bankiru_lead_offers_expired');
        loadFreshFallback();
        return;
      }

      // Заголовок: номер заявки сервер не знает (в URL — непрогнозируемый токен, не id),
      // поэтому реальный leadId и сумма (smsApprovalAmount — тот же продукт, что в тексте SMS;
      // фолбэк — максимум по списку) подставляются здесь, после ответа API
      const headerAmount = res.smsApprovalAmount
        || Math.max(...list.map(p => p.approvalAmountMax || 0));
      if (res.leadId) {
        $header.find('[data-lead-id]').text(res.leadId);
      }
      $header.find('[data-lead-amount]').text(headerAmount.toLocaleString('ru-RU'));
      $header.show();

      // Карточки
      const cardsHtml = list.map(renderCard).join('');
      $cardsContainer.html((wrapperOpen || '') + cardsHtml + (wrapperClose || ''));

      // Переместить #promo после секции Banki.ru — перед обычными продуктами
      const promoEl = document.getElementById('promo');
      if (promoEl) {
        $section[0].parentNode.insertBefore(promoEl, $section[0].nextSibling);
        promoEl.style.marginTop = '40px';
        promoEl.style.marginBottom = '24px';
        promoEl.style.paddingTop = '0';
        promoEl.style.paddingBottom = '0';
      }

      startLeadCountdownTimers();
      // Показ отправляется параллельно и клик его не ждёт: атрибуция клика
      // опирается на smsToken, а не на сохранённое событие (см. bindLeadClicks).
      trackLeadImpression(res.leadId, res.visitId, list.length, leadToken);
      bindLeadClicks(res.visitId, false);

    })
    .catch(() => {
      $preloader.hide();
      $section.hide();
      showHeaderMessage('Ошибка загрузки');
      postLeadOutcome('bankiru_lead_load_error');
    });

  // B24-92372: чем закончилась страница для тех, кто до карточек не дошёл.
  // Без этого «Запрос ссылки → Показ» не разложить на причины: по прод-данным
  // 21 из 114 перешедших показа не имеют, и по ним не видно, упёрлись они в
  // ошибку, в долгий подбор или просто ушли раньше отрисовки.
  function postLeadOutcome(type) {
    postEvent({ type, smsToken: leadToken, occurredAt: new Date().toISOString() });
  }

  function loadFreshFallback(attempt = 0) {
    if (attempt === 0) {
      $preloader.show();
      $section.show();
      showHeaderMessage('Подбираем актуальные продукты');
    }

    fetch((buildApiUrl('/api/bankiru/get') || '/api/bankiru/get') + '?t=' + encodeURIComponent(leadToken), { method: 'GET' })
      .then(r => r.json())
      .then(res => {
        const list = Array.isArray(res?.prescoringList) ? res.prescoringList : [];
        if (res?.result === 'success' && list.length) {
          $preloader.hide();
          const cardsHtml = list.map(renderCard).join('');
          $cardsContainer.html((wrapperOpen || '') + cardsHtml + (wrapperClose || ''));
          $section.show();
          // Переместить #promo после секции Banki.ru
          const promoEl = document.getElementById('promo');
          if (promoEl) {
            $section[0].parentNode.insertBefore(promoEl, $section[0].nextSibling);
            promoEl.style.marginTop = '40px';
            promoEl.style.marginBottom = '24px';
            promoEl.style.paddingTop = '0';
            promoEl.style.paddingBottom = '0';
          }
          startLeadCountdownTimers();
          // Тот же порядок, что в основном сценарии. leadToken здесь особенно важен:
          // res.leadId — новый лид, и без токена показ не связался бы с СМС ничем.
          // visitId: с B24-92372 его возвращает и /api/bankiru/get, но подстраховка
          // визитом со страницы остаётся — на CDN какое-то время живут старые
          // бандлы и отвечают старые ответы. Без визита показ и клик оказывались
          // в разных визитах и не сходились друг с другом.
          const fallbackVisitId = res.visitId || window.__visitId || null;
          trackLeadImpression(res.leadId, fallbackVisitId, list.length, leadToken);
          bindLeadClicks(fallbackVisitId, true);
          return;
        }

        if (res?.result === 'pending' && attempt < 20) {
          window.setTimeout(() => loadFreshFallback(attempt + 1), LEAD_FALLBACK_POLL_INTERVAL_MS);
          return;
        }

        $preloader.hide();
        $section.hide();
        showHeaderMessage('По вашей заявке продукты не найдены');
        // Различаем два разных отказа: подбор не уложился в лимит ожидания
        // (клиент смотрел на прелоадер и ушёл) и подбор завершился пустым.
        postLeadOutcome(res?.result === 'pending'
          ? 'bankiru_lead_fallback_timeout'
          : 'bankiru_lead_no_products');
      })
      .catch(() => {
        $preloader.hide();
        $section.hide();
        showHeaderMessage('Ошибка загрузки');
        postLeadOutcome('bankiru_lead_load_error');
      });
  }

  function leadPost(path, body) {
    return fetch(buildApiUrl(path) || path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());
  }

  // impressionReady больше не участвует в отправке клика. Ждать его было
  // бессмысленно: /api/bankiru/event отвечает 204 сразу после dispatch в очередь
  // (#[AsMessage('async_banki_ru_events')]), то есть ответ HTTP не означает, что
  // показ сохранён, — гонка оставалась, а клиент простаивал на пути к партнёру. Атрибуция клика теперь не требует записи показа
  // (BankiRuController::lastImpressionIsFromLeadPage), поэтому ждать нечего.
  function bindLeadClicks(visitId, isFallback = false) {
    // window.open ПОСЛЕ асинхронного fetch блокируется браузерами как popup —
    // открываем вкладку синхронно в обработчике клика, URL задаём после ответа.
    const go = (productId) => {
      // См. bindClicks: без этого двойной тап шлёт два клика и открывает две вкладки.
      if (clicksInFlight.has(productId)) return;
      clicksInFlight.add(productId);
      const release = setTimeout(() => clicksInFlight.delete(productId), CLICK_GUARD_MS);
      const done = () => { clearTimeout(release); clicksInFlight.delete(productId); };

      const win = window.open('', '_blank');

      Promise.resolve()
        .then(() => leadPost('/api/bankiru/click', {
          bankiruProductId: productId,
          // B24-92552: /api/bankiru/lead отдаёт visitId; fallback-эндпоинт
          // /api/bankiru/get стал отдавать его только с B24-92372, поэтому визит
          // со страницы остаётся запасным вариантом — иначе клики из fallback
          // уходят без привязки к визиту.
          visitId: visitId || window.__visitId || null,
          leadToken: isFallback ? null : leadToken,
          // B24-92368: атрибуция клика к СМС — шлётся всегда, включая fallback
          // (в отличие от leadToken, не участвует в валидации привязки продукта к лиду).
          smsToken: leadToken,
        }))
        .then(res => {
          done();
          if (res && res.result === 'success' && res.partnerUrl) {
            trackGoal('bankiru_lead_offer_click', { bankiruProductId: productId });
            if (win) {
              win.location.href = res.partnerUrl;
            } else {
              window.location.href = res.partnerUrl;
            }
          } else if (win) {
            win.close();
          }
        })
        .catch(() => { done(); if (win) win.close(); });
    };

    $cardsContainer.off('click.bankiru')
      .on('click.bankiru', '.bankiru-card__btn', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        const $wrapper = $(this).closest('[data-product-id], [data-product-code]');
        const productId = $wrapper.data('product-id') || $wrapper.data('product-code');
        if (productId) go(productId);
      })
      .on('click.bankiru', '[data-product-id], [data-product-code]', function (e) {
        if ($(e.target).closest('.bankiru-card__btn').length) return;
        const $wrapper = $(e.target).closest('[data-product-id], [data-product-code]');
        if (!$wrapper.length) return;
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        const productId = $wrapper.data('product-id') || $wrapper.data('product-code');
        if (productId) go(productId);
      });
  }

  function startLeadCountdownTimers() {
    $('.bankiru-card__expire').each(function () {
      const expireTimestamp = $(this).data('expire');
      if (!expireTimestamp) return;

      const $countdown = $(this).find('.bankiru-card__countdown');
      const expireDate = new Date(expireTimestamp);

      const updateTimer = () => {
        const diff = expireDate - new Date();

        if (diff > 3600000) { $(this).hide(); return; }

        if (diff <= 0) {
          $(this).closest('.tpl-offer__wrapper, [data-product-id], [data-product-code]').remove();
          const remaining = $cardsContainer.find('[data-product-id], [data-product-code]').length;
          if (remaining === 0) {
            $section.hide();
            showHeaderMessage('По вашей заявке продукты не найдены');
          }
          return;
        }

        $(this).show();
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        $countdown.text(
          String(hours).padStart(2, '0') + ':' +
          String(minutes).padStart(2, '0') + ':' +
          String(seconds).padStart(2, '0')
        );
      };

      updateTimer();
      setInterval(updateTimer, 1000);
    });
  }
}
